import {
  Scenario,
  CreateScenarioDto,
  UpdateScenarioDto,
  ScenarioFilterDto,
  ScenarioTemplate,
  CreateTemplateInput,
  UpdateTemplateInput,
  ScenarioPerformance,
  PerformanceMetrics,
  ExecutionStats,
  DateRange,
  PerformanceFilter,
  ScenarioStats,
  TemplateCategory,
  TemplateValidationResult,
  PaginatedResponse,
  ApiResponse,
} from "../types/scenario";
import { logger } from "./logging";

// Base GraphQL configuration
const GRAPHQL_URL =
  import.meta.env.VITE_GRAPHQL_URL || "http://localhost:4000/graphql";

// Helper function to make GraphQL requests
const graphqlRequest = async <T>(
  query: string,
  variables?: any,
): Promise<T> => {
  const response = await fetch(GRAPHQL_URL, {
    method: "POST",
    // Must include credentials so browser sends httpOnly auth cookies
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || "GraphQL error");
  }

  return result.data;
};

// GraphQL Queries
const GET_SCENARIOS_QUERY = `
  query GetScenarios($page: Int, $limit: Int, $filter: ScenarioFilter) {
    scenarios(page: $page, limit: $limit, filter: $filter) {
      edges {
        node {
          id
          name
          description
          category
          status
          createdAt
          updatedAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const GET_SCENARIO_QUERY = `
  query GetScenario($id: ID!) {
    scenario(id: $id) {
      id
      name
      description
      category
      status
      createdAt
      updatedAt
    }
  }
`;

const GET_TEMPLATES_QUERY = `
  query GetTemplates($category: String, $riskLevel: String, $timeFrame: String, $difficulty: String, $tags: [String!], $author: String) {
    templates(category: $category, riskLevel: $riskLevel, timeFrame: $timeFrame, difficulty: $difficulty, tags: $tags, author: $author) {
      id
      name
      description
      category
      riskLevel
      timeFrame
      difficulty
      estimatedReturn
      maxDrawdown
      timeHorizon
    }
  }
`;

const GET_TEMPLATE_CATEGORIES_QUERY = `
  query GetTemplateCategories {
    templateCategories {
      name
      count
      description
    }
  }
`;

// GraphQL Mutations
const CREATE_SCENARIO_MUTATION = `
  mutation CreateScenario($input: CreateScenarioInput!) {
    createScenario(input: $input) {
      id
      name
      description
      category
      status
      createdAt
    }
  }
`;

const UPDATE_SCENARIO_MUTATION = `
  mutation UpdateScenario($id: ID!, $input: UpdateScenarioInput!) {
    updateScenario(id: $id, input: $input) {
      id
      name
      description
      category
      status
      updatedAt
    }
  }
`;

const DELETE_SCENARIO_MUTATION = `
  mutation DeleteScenario($id: ID!) {
    deleteScenario(id: $id)
  }
`;

const CREATE_TEMPLATE_MUTATION = `
  mutation CreateTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input) {
      id
      name
      description
      category
      riskLevel
      timeFrame
      difficulty
      estimatedReturn
      maxDrawdown
      timeHorizon
    }
  }
`;

// Scenario API calls using GraphQL
export const scenarioService = {
  // Get all scenarios with filtering and pagination
  async getScenarios(
    filter: ScenarioFilterDto = {},
  ): Promise<PaginatedResponse<Scenario>> {
    const { page = 1, limit = 10, category, status } = filter;

    logger.debug(
      "Calling GraphQL with filter",
      { method: "getScenarios" },
      { filter },
    );

    try {
      const data = await graphqlRequest<{ scenarios: any }>(
        GET_SCENARIOS_QUERY,
        { page, limit, filter: { category, status } },
      );

      logger.debug(
        "GraphQL raw response",
        { method: "getScenarios" },
        { data },
      );

      // Convert GraphQL connection format to PaginatedResponse format
      const scenarios = data.scenarios.edges.map((edge: any) => edge.node);
      const result = {
        items: scenarios,
        total: data.scenarios.totalCount,
        page,
        limit,
        totalPages: Math.ceil(data.scenarios.totalCount / limit),
      };

      logger.debug(
        "Converted response",
        { method: "getScenarios" },
        { result },
      );
      return result;
    } catch (error) {
      logger.error("GraphQL error", { method: "getScenarios" }, error);
      // Return empty result on error
      return {
        items: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    }
  },

  // Get a single scenario by ID
  async getScenario(id: string): Promise<Scenario> {
    const data = await graphqlRequest<{ scenario: Scenario }>(
      GET_SCENARIO_QUERY,
      { id },
    );

    return data.scenario;
  },

  // Create a new scenario
  async createScenario(data: CreateScenarioDto): Promise<Scenario> {
    const result = await graphqlRequest<{ createScenario: Scenario }>(
      CREATE_SCENARIO_MUTATION,
      { input: data },
    );

    return result.createScenario;
  },

  // Update an existing scenario
  async updateScenario(id: string, data: UpdateScenarioDto): Promise<Scenario> {
    const result = await graphqlRequest<{ updateScenario: Scenario }>(
      UPDATE_SCENARIO_MUTATION,
      { id, input: data },
    );

    return result.updateScenario;
  },

  // Delete a scenario
  async deleteScenario(id: string): Promise<void> {
    await graphqlRequest<{ deleteScenario: boolean }>(
      DELETE_SCENARIO_MUTATION,
      { id },
    );
  },

  // Activate a scenario
  async activateScenario(id: string): Promise<Scenario> {
    const result = await graphqlRequest<{ activateScenario: Scenario }>(
      `mutation ActivateScenario($id: ID!) { activateScenario(id: $id) { id status } }`,
      { id },
    );

    return result.activateScenario;
  },

  // Deactivate a scenario
  async deactivateScenario(id: string): Promise<Scenario> {
    const result = await graphqlRequest<{ deactivateScenario: Scenario }>(
      `mutation DeactivateScenario($id: ID!) { deactivateScenario(id: $id) { id status } }`,
      { id },
    );

    return result.deactivateScenario;
  },

  // Clone a scenario
  async cloneScenario(id: string, name?: string): Promise<Scenario> {
    const result = await graphqlRequest<{ cloneScenario: Scenario }>(
      `mutation CloneScenario($id: ID!, $name: String) { cloneScenario(id: $id, name: $name) { id name } }`,
      { id, name },
    );

    return result.cloneScenario;
  },

  // Get scenario templates
  async getScenarioTemplates(category?: string): Promise<ScenarioTemplate[]> {
    try {
      const data = await graphqlRequest<{
        scenarioTemplates: ScenarioTemplate[];
      }>(GET_TEMPLATES_QUERY, { category });

      return data.templates || [];
    } catch (error) {
      logger.error(
        "Error fetching scenario templates",
        { method: "getTemplates" },
        error,
      );
      return [];
    }
  },

  // Get active scenarios for current user
  async getActiveScenarios(): Promise<Scenario[]> {
    const data = await graphqlRequest<{ activeScenarios: Scenario[] }>(
      `query GetActiveScenarios { activeScenarios { id name status } }`,
    );

    return data.activeScenarios;
  },

  // Get scenario statistics for current user
  async getScenarioStats(): Promise<ScenarioStats> {
    const data = await graphqlRequest<{ scenarioStats: ScenarioStats }>(
      `query GetScenarioStats { scenarioStats { total active draft completed templates } }`,
    );

    return data.scenarioStats;
  },
};

// Template API calls using GraphQL
export const templateService = {
  // Get all templates with filtering
  async getTemplates(
    filters: {
      category?: string;
      riskLevel?: string;
      timeFrame?: string;
      difficulty?: string;
      tags?: string;
      author?: string;
    } = {},
  ): Promise<ScenarioTemplate[]> {
    const data = await graphqlRequest<{ templates: ScenarioTemplate[] }>(
      GET_TEMPLATES_QUERY,
      filters,
    );

    return data.templates;
  },

  // Get template categories
  async getTemplateCategories(): Promise<TemplateCategory[]> {
    const data = await graphqlRequest<{
      templateCategories: TemplateCategory[];
    }>(GET_TEMPLATE_CATEGORIES_QUERY);

    return data.templateCategories;
  },

  // Get popular templates
  async getPopularTemplates(limit: number = 10): Promise<ScenarioTemplate[]> {
    const data = await graphqlRequest<{ popularTemplates: ScenarioTemplate[] }>(
      `query GetPopularTemplates($limit: Int) { popularTemplates(limit: $limit) { id name category } }`,
      { limit },
    );

    return data.popularTemplates;
  },

  // Search templates
  async searchTemplates(
    query: string,
    filters: {
      category?: string;
      riskLevel?: string;
      timeFrame?: string;
    } = {},
  ): Promise<ScenarioTemplate[]> {
    const data = await graphqlRequest<{ searchTemplates: any }>(
      `query SearchTemplates($query: String!, $category: String, $riskLevel: String, $timeFrame: String) { 
        searchTemplates(query: $query, category: $category, riskLevel: $riskLevel, timeFrame: $timeFrame) { 
          templates { id name category }
          totalCount
        } 
      }`,
      { query, ...filters },
    );

    return data.searchTemplates.templates;
  },

  // Get template by ID
  async getTemplateById(id: string): Promise<ScenarioTemplate> {
    const data = await graphqlRequest<{ template: ScenarioTemplate }>(
      `query GetTemplate($id: ID!) { template(id: $id) { id name category riskLevel timeFrame difficulty } }`,
      { id },
    );

    return data.template;
  },

  // Create a new template
  async createTemplate(data: CreateTemplateInput): Promise<ScenarioTemplate> {
    const result = await graphqlRequest<{ createTemplate: ScenarioTemplate }>(
      CREATE_TEMPLATE_MUTATION,
      { input: data },
    );

    return result.createTemplate;
  },

  // Update an existing template
  async updateTemplate(
    id: string,
    data: UpdateTemplateInput,
  ): Promise<ScenarioTemplate> {
    const result = await graphqlRequest<{ updateTemplate: ScenarioTemplate }>(
      `mutation UpdateTemplate($id: ID!, $input: UpdateTemplateInput!) { 
        updateTemplate(id: $id, input: $input) { id name category riskLevel timeFrame difficulty } 
      }`,
      { id, input: data },
    );

    return result.updateTemplate;
  },

  // Delete a template
  async deleteTemplate(id: string): Promise<void> {
    await graphqlRequest<{ deleteTemplate: string }>(
      `mutation DeleteTemplate($id: ID!) { deleteTemplate(id: $id) }`,
      { id },
    );
  },

  // Validate a template
  async validateTemplate(id: string): Promise<TemplateValidationResult> {
    const result = await graphqlRequest<{
      validateTemplate: TemplateValidationResult;
    }>(
      `query ValidateTemplate($id: ID!) { validateTemplate(id: $id) { isValid errors } }`,
      { id },
    );

    return result.validateTemplate;
  },

  // Apply a template to create a scenario
  async applyTemplate(id: string, customizations: any): Promise<Scenario> {
    const result = await graphqlRequest<{ applyTemplate: Scenario }>(
      `mutation ApplyTemplate($id: ID!, $customizations: TemplateCustomizationInput!) { 
        applyTemplate(id: $id, customizations: $customizations) { id name category status } 
      }`,
      { id, customizations },
    );

    return result.applyTemplate;
  },
};

// Performance API calls using GraphQL
export const performanceService = {
  // Get performance for a specific scenario
  async getPerformance(
    scenarioId: string,
    startDate?: Date,
    endDate?: Date,
  ): Promise<PerformanceMetrics> {
    const data = await graphqlRequest<{
      scenarioPerformance: PerformanceMetrics;
    }>(
      `query GetPerformance($scenarioId: ID!, $startDate: String, $endDate: String) { 
        scenarioPerformance(scenarioId: $scenarioId, startDate: $startDate, endDate: $endDate) { 
          totalReturn sharpeRatio maxDrawdown 
        } 
      }`,
      {
        scenarioId,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      },
    );

    return data.scenarioPerformance;
  },

  // Generate performance report
  async generateReport(
    scenarioId: string,
    startDate?: Date,
    endDate?: Date,
  ): Promise<ScenarioPerformance> {
    const data = await graphqlRequest<{
      performanceReport: ScenarioPerformance;
    }>(
      `query GenerateReport($scenarioId: ID!, $startDate: String, $endDate: String) { 
        performanceReport(scenarioId: $scenarioId, startDate: $startDate, endDate: $endDate) { 
          id totalReturn trades 
        } 
      }`,
      {
        scenarioId,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      },
    );

    return data.performanceReport;
  },

  // Get performance history
  async getPerformanceHistory(
    scenarioId: string,
    limit: number = 100,
  ): Promise<ScenarioPerformance[]> {
    const data = await graphqlRequest<{
      performanceHistory: ScenarioPerformance[];
    }>(
      `query GetPerformanceHistory($scenarioId: ID!, $limit: Int) { 
        performanceHistory(scenarioId: $scenarioId, limit: $limit) { id totalReturn } 
      }`,
      { scenarioId, limit },
    );

    return data.performanceHistory;
  },

  // Compare multiple scenarios
  async compareScenarios(
    scenarioIds: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<ScenarioPerformance[]> {
    const data = await graphqlRequest<{
      compareScenarios: ScenarioPerformance[];
    }>(
      `query CompareScenarios($scenarioIds: [ID!]!, $startDate: String, $endDate: String) { 
        compareScenarios(scenarioIds: $scenarioIds, startDate: $startDate, endDate: $endDate) { 
          id totalReturn 
        } 
      }`,
      {
        scenarioIds,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      },
    );

    return data.compareScenarios;
  },
};
